import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Collapse
} from '@mui/material';
import {
  GetApp as InstallIcon,
  Close as CloseIcon,
  Computer as DesktopIcon,
  PhoneAndroid as MobileIcon
} from '@mui/icons-material';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [hasShownPrompt, setHasShownPrompt] = useState(false);

  // Check if PWA is installable
  const isPWAInstallable = () => {
    return 'serviceWorker' in navigator && 
           window.matchMedia('(display-mode: standalone)').matches === false &&
           window.navigator.standalone !== true;
  };

  useEffect(() => {
    // Check if app is already installed
    const checkInstallStatus = () => {
      if (window.matchMedia('(display-mode: standalone)').matches ||
          window.navigator.standalone === true) {
        setIsInstalled(true);
        return;
      }
      
      // Check if app is installed via other methods
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then((registration) => {
          if (registration.active) {
            setIsInstalled(true);
          }
        });
      }
    };

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Only show prompt if user hasn't seen it before and hasn't installed
      if (!hasShownPrompt && !isInstalled) {
        setShowInstallPrompt(true);
        console.log('PWA: Install prompt available');
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
          setShowInstallPrompt(false);
          setHasShownPrompt(true);
          // Save to localStorage so it doesn't show again
          localStorage.setItem('pwaPromptShown', 'true');
        }, 5000);
      }
    };

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setHasShownPrompt(true);
      localStorage.setItem('pwaPromptShown', 'true');
      console.log('PWA was installed');
    };

    // Check if user has already seen the prompt
    const promptShown = localStorage.getItem('pwaPromptShown');
    if (promptShown) {
      setHasShownPrompt(true);
    }

    checkInstallStatus();

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [hasShownPrompt, isInstalled]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      setShowInstructions(true);
      return;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        setShowInstallPrompt(false);
      } else {
        console.log('User dismissed the install prompt');
      }
      
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Installation failed:', error);
      setShowInstructions(true);
    }
  };

  const handleClose = () => {
    setShowInstallPrompt(false);
    setShowInstructions(false);
  };



  if (isInstalled) {
    return null;
  }

  // Always show manual install option if PWA is installable
  const showManualInstall = isPWAInstallable() && !showInstallPrompt && !showInstructions && !hasShownPrompt && !isInstalled;

  return (
    <>
      {/* Manual Install Button - Only show once for new users */}
      {showManualInstall && (
        <Paper 
          elevation={3} 
          sx={{ 
            position: 'fixed', 
            bottom: 20, 
            left: 20, 
            zIndex: 1000,
            p: 2,
            maxWidth: 300,
            bgcolor: 'success.main',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <InstallIcon />
            <Typography variant="h6">Install App</Typography>
          </Box>
          
          <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
            Get quick access to Traders Journal
          </Typography>
          
          <Button
            variant="contained"
            color="secondary"
            startIcon={<InstallIcon />}
            onClick={() => {
              setShowInstructions(true);
              setHasShownPrompt(true);
              localStorage.setItem('pwaPromptShown', 'true');
            }}
            fullWidth
            sx={{ 
              bgcolor: 'white', 
              color: 'success.main',
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            Show Install Guide
          </Button>
        </Paper>
      )}

      {/* Install Prompt */}
      <Collapse in={showInstallPrompt}>
        <Paper 
          elevation={3} 
          sx={{ 
            position: 'fixed', 
            bottom: 20, 
            right: 20, 
            zIndex: 1000,
            p: 2,
            maxWidth: 350,
            bgcolor: 'primary.main',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <InstallIcon />
            <Typography variant="h6">Install App</Typography>
            <IconButton 
              size="small" 
              onClick={handleClose}
              sx={{ color: 'white', ml: 'auto' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
            Install Traders Journal for quick access and offline use
          </Typography>
          
          <Button
            variant="contained"
            color="secondary"
            startIcon={<InstallIcon />}
            onClick={handleInstallClick}
            fullWidth
            sx={{ 
              bgcolor: 'white', 
              color: 'primary.main',
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            Install Now
          </Button>
        </Paper>
      </Collapse>

      {/* Manual Installation Instructions */}
      <Collapse in={showInstructions}>
        <Paper 
          elevation={3} 
          sx={{ 
            position: 'fixed', 
            bottom: 20, 
            right: 20, 
            zIndex: 1000,
            p: 2,
            maxWidth: 400,
            bgcolor: 'background.paper'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h6">Install Instructions</Typography>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <DesktopIcon color="primary" />
              Desktop (Chrome/Edge)
            </Typography>
            <Typography variant="body2" sx={{ ml: 3, mb: 1 }}>
              1. Click the install icon in the address bar
            </Typography>
            <Typography variant="body2" sx={{ ml: 3, mb: 1 }}>
              2. Or press Ctrl+Shift+I and click "Install"
            </Typography>
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <MobileIcon color="primary" />
              Mobile (Chrome/Safari)
            </Typography>
            <Typography variant="body2" sx={{ ml: 3, mb: 1 }}>
              1. Tap the share button
            </Typography>
            <Typography variant="body2" sx={{ ml: 3, mb: 1 }}>
              2. Select "Add to Home Screen"
            </Typography>
          </Box>
          
          <Chip 
            label="PWA Ready" 
            color="success" 
            size="small" 
            sx={{ fontSize: '0.75rem' }}
          />
        </Paper>
      </Collapse>
    </>
  );
};

export default InstallPrompt;
